import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { Buildings2Icon, ClockIcon, PencilWritingIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import { CustomSurveyTemplate, TemplateScope } from '../../../api/customSurveyTemplates';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { categoriesOf, daysSinceUsed } from '../templatesView';
import classes from '../CustomSurveyTemplatesPage.module.css';

const usedKey = (days: number | null) => {
  if (days === null) return { key: 'customSurveys.templates.card.neverUsed' };
  if (days === 0) return { key: 'customSurveys.templates.card.usedToday' };
  if (days < 30) return { key: 'customSurveys.templates.card.usedDaysAgo', count: days };
  return { key: 'customSurveys.templates.card.usedMonthsAgo', count: Math.round(days / 30) };
};

export const TemplateCard = ({ template }: { template: CustomSurveyTemplate }) => {
  const { t } = useTranslation();
  const isTeam = template.scope === TemplateScope.TEAM;
  const surveys = template.surveysCreated ?? 0;
  const used = usedKey(daysSinceUsed(template.lastUsed));
  const templateId = template.id;

  return (
    <li className={classes.card}>
      <div className={classes.tags}>
        <Tag data-size="sm" data-color={isTeam ? 'brand1' : 'accent'} className={classes.scopeTag}>
          {isTeam ? <PersonGroupIcon aria-hidden /> : <Buildings2Icon aria-hidden />}
          {isTeam
            ? t('customSurveys.templates.card.team', { name: template.scopeTeamName ?? '' }).trim()
            : t('customSurveys.templates.card.company')}
        </Tag>
        {categoriesOf(template).map((category) => (
          <Tag key={category} data-size="sm" data-color="neutral">
            {category}
          </Tag>
        ))}
        {template.isArchived && (
          <Tag data-size="sm" data-color="warning">
            {t('customSurveys.templates.card.archived')}
          </Tag>
        )}
      </div>

      <div className={classes.cardText}>
        <Heading level={2} data-size="xs">
          <Link to={buildRoute(Routes.CustomSurveyTemplateDetail, { templateId })} className={classes.cardLink}>
            {template.name}
          </Link>
        </Heading>
        {template.description && (
          <Paragraph data-size="sm" className={classes.subtle}>
            {template.description}
          </Paragraph>
        )}
      </div>

      <dl className={classes.cardStats}>
        <div>
          <dt>{t('customSurveys.templates.card.questions')}</dt>
          <dd>{template.questions.length}</dd>
        </div>
        <div>
          <dt>{t('customSurveys.templates.card.surveys', { count: surveys })}</dt>
          <dd>{surveys}</dd>
        </div>
      </dl>

      <div className={classes.cardFooter}>
        <span className={classes.used}>
          <ClockIcon aria-hidden />
          {t(used.key, { count: used.count })}
        </span>
        <Button asChild variant="secondary" data-size="sm">
          <Link
            to={buildRoute(Routes.EditCustomSurveyTemplate, { templateId })}
            aria-label={t('customSurveys.templates.card.editFor', { name: template.name })}
          >
            <PencilWritingIcon aria-hidden />
            {t('customSurveys.templates.card.edit')}
          </Link>
        </Button>
      </div>
    </li>
  );
};
